import { HomeIcon, Newspaper, PlayIcon } from 'lucide-react';
import React, { useState } from 'react';
import { MdLiveTv, MdProductionQuantityLimits } from 'react-icons/md';
import { useNavigate } from 'react-router-dom';

function Header() {
    // ✅ Define navigate FIRST before using it anywhere
    const navigate = useNavigate();
    const [mobileMenuOpen, setMobileMenuOpen] = useState(false);

    const menuItems2 = [
        { hindi: 'होम',       english: 'Home',     icon: HomeIcon,                    action: () => navigate('/') },
        { hindi: 'शोज़',      english: 'Shows',    icon: MdLiveTv,                    action: () => navigate('/') },
        { hindi: 'देखिए',     english: 'Watch',    icon: PlayIcon,                    action: () => navigate('/allvideos') },
        { hindi: 'प्रोडक्ट्स', english: 'Products', icon: MdProductionQuantityLimits, action: () => navigate('/') },
        { hindi: 'ई-पेपर',   english: 'EPaper',   icon: Newspaper,                   action: () => navigate('/epaper') },
    ];

    const menuItems = [
        { hindi: 'राजनीति',      english: 'Politics',       route: '/category/Politics' },
        { hindi: 'व्यापार',      english: 'Business',       route: '/category/Business' },
        { hindi: 'खेल',          english: 'Sports',         route: '/category/Sports' },
        { hindi: 'अंतर्राष्ट्रीय', english: 'International', route: '/category/World' },
        { hindi: 'मनोरंजन',      english: 'Entertainment',  route: '/category/Entertainment' },
        { hindi: 'शिक्षा',       english: 'Education',      route: '/category/Education' },
        { hindi: 'संपर्क',       english: 'Contact',        route: '/contact' },
    ];

    return (
        <>
            {/* ── Top Bar ── */}
            <div className="bg-[#ef0501] border-b border-red-700">
                <div className="max-w-7xl mx-auto px-2 sm:px-4 lg:px-8">

                    {/* Desktop */}
                    <nav className="hidden lg:flex space-x-6 xl:space-x-8 justify-center py-2">
                        {menuItems2.map((item) => {
                            const Icon = item.icon;
                            return (
                                <button
                                    key={item.english}
                                    onClick={item.action}
                                    className="text-xs xl:text-sm text-white hover:text-black font-medium transition-colors duration-200 relative group py-1 flex items-center gap-1.5 xl:gap-2"
                                >
                                    <Icon className="w-3.5 h-3.5 xl:w-4 xl:h-4 flex-shrink-0" />
                                    <span className="whitespace-nowrap">{item.hindi}</span>
                                    <span className="absolute bottom-0 left-0 w-0 h-0.5 bg-white group-hover:w-full transition-all duration-200"></span>
                                </button>
                            );
                        })}
                    </nav>

                    {/* Mobile - horizontal scroll */}
                    <nav className="lg:hidden flex gap-2 overflow-x-auto py-2 scrollbar-hide">
                        {menuItems2.map((item) => {
                            const Icon = item.icon;
                            return (
                                <button
                                    key={item.english}
                                    onClick={item.action}
                                    className="text-xs sm:text-sm text-white hover:text-black font-medium transition-colors duration-200 whitespace-nowrap px-2 sm:px-3 py-1 flex items-center gap-1 sm:gap-1.5 flex-shrink-0"
                                >
                                    <Icon className="w-3.5 h-3.5 sm:w-4 sm:h-4" />
                                    <span>{item.hindi}</span>
                                </button>
                            );
                        })}
                    </nav>
                </div>
            </div>

            {/* ── Main Header ── */}
            <header className="bg-white shadow-md sticky top-0 z-50">
                <div className="max-w-7xl mx-auto px-2 sm:px-4 lg:px-8">
                    <div className="flex justify-between items-center py-2 sm:py-3 md:py-2">

                        {/* Logo */}
                        <div
                            onClick={() => navigate('/')}
                            className="flex items-center space-x-2 cursor-pointer flex-shrink-0"
                        >
                            <div className="p-1.5 sm:p-2 md:p-3 rounded-lg shadow-sm">
                                <img
                                    src="/assets/images/logo-ij.jpeg"
                                    alt="logo"
                                    className="h-7 sm:h-8 md:h-10 w-auto object-contain"
                                />
                            </div>
                        </div>

                        {/* Desktop Nav */}
                        <nav className="hidden md:flex items-center">
                            <ul className="flex space-x-3 lg:space-x-4 xl:space-x-6 list-none m-0 p-0">
                                {menuItems.map((item) => (
                                    <li
                                        key={item.english}
                                        onClick={() => navigate(item.route)}  // ✅ each item uses its own route
                                        className="cursor-pointer"
                                    >
                                        <span className="text-xs lg:text-sm xl:text-base text-gray-700 hover:text-red-600 font-medium transition-colors duration-200 relative group whitespace-nowrap inline-block py-1">
                                            {item.hindi}
                                            <span className="absolute bottom-0 left-0 w-0 h-0.5 bg-red-600 group-hover:w-full transition-all duration-200"></span>
                                        </span>
                                    </li>
                                ))}
                            </ul>
                        </nav>

                        {/* Mobile Menu Toggle */}
                        <button
                            className="md:hidden p-1.5 sm:p-2 rounded-md text-gray-700 hover:bg-gray-100 transition-colors duration-200 flex-shrink-0"
                            onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
                            aria-label="Toggle mobile menu"
                            aria-expanded={mobileMenuOpen}
                        >
                            <svg className="w-5 h-5 sm:w-6 sm:h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                {mobileMenuOpen ? (
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                                ) : (
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6h16M4 12h16M4 18h16" />
                                )}
                            </svg>
                        </button>
                    </div>

                    {/* Mobile Dropdown */}
                    {mobileMenuOpen && (
                        <nav className="md:hidden pb-3 sm:pb-4 space-y-1 animate-slideDown">
                            <ul className="list-none m-0 p-0">
                                {menuItems.map((item) => (
                                    <li key={item.english}>
                                        <button
                                            className="block w-full text-left px-3 sm:px-4 py-2.5 sm:py-3 text-sm sm:text-base text-gray-700 hover:bg-red-50 hover:text-red-600 rounded-md transition-colors duration-200 font-medium"
                                            onClick={() => {
                                                navigate(item.route); // ✅ navigate properly
                                                setMobileMenuOpen(false);
                                            }}
                                        >
                                            {item.hindi}
                                        </button>
                                    </li>
                                ))}
                            </ul>
                        </nav>
                    )}
                </div>
            </header>

            <style>{`
                .scrollbar-hide::-webkit-scrollbar { display: none; }
                .scrollbar-hide { -ms-overflow-style: none; scrollbar-width: none; }
                @keyframes slideDown {
                    from { opacity: 0; transform: translateY(-10px); }
                    to   { opacity: 1; transform: translateY(0); }
                }
                .animate-slideDown { animation: slideDown 0.2s ease-out; }
            `}</style>
        </>
    );
}

export default Header;